import * as React from 'react';

import {
    CustomConfig,
    NavigationData,
    PageConstructor,
    PageConstructorProvider,
    PageConstructorProviderProps,
    PageContent,
} from '@gravity-ui/page-constructor';

import {MetaWrapper} from '../../components/MetaWrapper/MetaWrapper';
import {PromptSignIn} from '../../components/PromptSignIn/PromptSignIn';
import {usePromptSignInProps} from '../../components/PromptSignIn/hooks/usePromptSignInProps';
import {FeedContext} from '../../contexts/FeedContext';
import {LikesContext} from '../../contexts/LikesContext';
import {SettingsContext} from '../../contexts/SettingsContext';
import {useExtendedComponentMap} from '../../hooks/useExtendedComponentMap';
import {
    FiltersConfig,
    GetPostsType,
    MetaProps,
    PostsProps,
    ToggleLikeCallbackType,
} from '../../models/common';

import './BlogPage.scss';

export type BlogPageProps = {
    content: PageContent;
    posts: PostsProps;
    filters?: FiltersConfig;
    navigation?: NavigationData;
    getPosts: GetPostsType;
    hasLikes?: boolean;
    toggleLike?: ToggleLikeCallbackType;
    metaData?: MetaProps;
    settings?: PageConstructorProviderProps;
    custom?: CustomConfig;
    pageCountForShowSupportButtons?: number;
    isSignedInUser?: boolean;
    // Required to enable Sign In on Post like
    onClickSignIn?: React.EventHandler<React.SyntheticEvent>;
};

export const BlogPage = ({
    content,
    posts,
    filters,
    getPosts,
    metaData,
    custom,
    hasLikes = false,
    toggleLike,
    navigation,
    settings,
    pageCountForShowSupportButtons,
    isSignedInUser = false,
    onClickSignIn,
}: BlogPageProps) => {
    const {isAnimationEnabled} = React.useContext(SettingsContext);
    const {requireSignIn, ...promptSignInProps} = usePromptSignInProps(onClickSignIn);

    const likesContextData = React.useMemo(
        () => ({toggleLike, hasLikes, isSignedInUser, requireSignIn}),
        [toggleLike, hasLikes, isSignedInUser, requireSignIn],
    );

    const actualComponentMap = useExtendedComponentMap(custom);

    return (
        <LikesContext.Provider value={likesContextData}>
            <FeedContext.Provider
                value={{
                    posts: posts.posts,
                    pinnedPost: posts.pinnedPost,
                    totalCount: posts.count,
                    filters,
                    getPosts,
                    pageCountForShowSupportButtons,
                }}
            >
                <PageConstructorProvider
                    {...settings}
                    projectSettings={{...(settings?.projectSettings || {}), isAnimationEnabled}}
                >
                    {metaData ? <MetaWrapper {...metaData} /> : null}
                    <PageConstructor
                        content={content}
                        custom={actualComponentMap}
                        navigation={navigation}
                    />
                </PageConstructorProvider>
            </FeedContext.Provider>
            <PromptSignIn {...promptSignInProps} />
        </LikesContext.Provider>
    );
};
